import { scheduleEventHasContactOrder } from "../../../../../../_shared/_logic/outreach-presentation"
import type {
  ContactOrder,
  ContactOrderPlanningPreview,
  ContactOrderPostalSelection,
} from "../../../../../../api/kehrblatt/contact-orders"
import type { CostBenefitEstimate } from "../../../../../../api/kehrblatt/cost-benefit"
import { resolveBatchApplyTargets } from "../../../_logic/batch-apply-targets"
import {
  buildDigitalChannelTogglesForContactOrderRow,
  buildPostalToggleOptionsForContactOrderRow,
  postalSelectionFromValue,
} from "../../../_logic/channels"
import type {
  ContactOrderAppointmentRow,
  ContactOrderPlanningRowPresentation,
  ContactOrderPlanningSubmissionPhase,
  ContactOrderPlanningSurfacePayloadBuilder,
  ContactOrderTableRow,
} from "../../../_logic/contact-orders-contract"
import { groupContactPointsByCustomerId } from "../../../_logic/contact-quality-tags"
import { hasUsefulBatchSearch, hasUsefulBatchSelectOptions } from "../../../_logic/filter-values"
import type { ContactOrderSelectionState } from "../../../_logic/selection-state"
import {
  communicationSelectionForPlanningRow,
  hasActiveCommunicationForPlanningRow,
  selectedCustomerIdsForPlanningRow,
  selectedDigitalChannelsForPlanningRow,
} from "../../../_logic/selection-state"
import { scheduleEventHasCompletedCustomerInformation } from "../_shared/_logic/customer-information-state"
import { buildContactOrderCreateIntent } from "../build-create-contact-orders-intent"
import { filterContactOrderPlanningRows, type ContactOrderPlanningFilterState } from "./filters"
import type { ContactOrderPlanningDraft } from "./planningDraft.svelte"
import { mergeRecipientContactPoints } from "./recipientQuickEditState"
import {
  contactOrderPlanningEventAllowsInteraction,
  contactOrderPlanningRowAllowsInteraction,
  pruneContactOrderPlanningSelectedIds,
} from "./rowEligibility"
import {
  buildContactOrderAppointmentRows,
  buildContactOrderAppointmentsRefresh,
  filterPendingContactOrderRows,
} from "./tableModel"
import {
  buildContactOrderFilterFields,
  buildContactOrderLoadingRows,
  buildContactOrderMetricsVisible,
  buildContactOrderTableLoading,
  buildSkippedWarningText,
} from "./tableState"

type PlanningProjectionInput = {
  organizationId: string
  draft: ContactOrderPlanningDraft
  localEvents: ScheduleEvent[]
  localWorkOrders: WorkOrder[]
  localCustomers: Customer[]
  localContactOrders: ContactOrder[]
  contactPoints: ContactPoint[]
  localContactPointUpdates: ContactPoint[]
  deletedContactPointIds: string[]
  properties: Property[]
  employeeOptions: Array<{ label: string; value: string }>
  planningPreviewByEventId: Record<string, ContactOrderPlanningPreview>
  planningPreviewErrorsByEventId: Record<string, string>
  dirtyScheduleEventIds: Record<string, true>
  rowPresentationByEventId: Record<string, ContactOrderPlanningRowPresentation>
  selectionState: ContactOrderSelectionState
  removedEventIds: string[]
  selectedIds: string[]
  contactFilters: ContactOrderPlanningFilterState
  allowedContactChannels: readonly string[]
  planningPreviewPendingEventIds: string[]
  planningInitialPreviewLoading: boolean
  planningCostBenefit: CostBenefitEstimate | null
  planningPreviewAttempted: boolean
  loading: boolean
  readOnly: boolean
  locked: boolean
  preconfigurationPending: boolean
  submissionPhase: ContactOrderPlanningSubmissionPhase
  caseIdByOrderId: Record<string, string | null>
  allowRemoveOnlySubmission: boolean
  buildCommitPayloads: ContactOrderPlanningSurfacePayloadBuilder
  createButtonLabel: string
  successMessage: (count: number) => string
}

export function createContactOrderPlanningProjection(source: () => PlanningProjectionInput) {
  const organizationId = $derived(source().organizationId)
  const draft = $derived(source().draft)
  const localEvents = $derived(source().localEvents)
  const localWorkOrders = $derived(source().localWorkOrders)
  const localCustomers = $derived(source().localCustomers)
  const localContactOrders = $derived(source().localContactOrders)
  const contactPoints = $derived(source().contactPoints)
  const localContactPointUpdates = $derived(source().localContactPointUpdates)
  const deletedContactPointIds = $derived(source().deletedContactPointIds)
  const properties = $derived(source().properties)
  const employeeOptions = $derived(source().employeeOptions)
  const planningPreviewByEventId = $derived(source().planningPreviewByEventId)
  const planningPreviewErrorsByEventId = $derived(source().planningPreviewErrorsByEventId)
  const dirtyScheduleEventIds = $derived(source().dirtyScheduleEventIds)
  const rowPresentationByEventId = $derived(source().rowPresentationByEventId)
  const selectionState = $derived(source().selectionState)
  const removedEventIds = $derived(source().removedEventIds)
  const selectedIds = $derived(source().selectedIds)
  const contactFilters = $derived(source().contactFilters)
  const allowedContactChannels = $derived(source().allowedContactChannels)
  const planningPreviewPendingEventIds = $derived(source().planningPreviewPendingEventIds)
  const planningInitialPreviewLoading = $derived(source().planningInitialPreviewLoading)
  const planningCostBenefit = $derived(source().planningCostBenefit)
  const planningPreviewAttempted = $derived(source().planningPreviewAttempted)
  const loading = $derived(source().loading)
  const readOnly = $derived(source().readOnly)
  const locked = $derived(source().locked)
  const preconfigurationPending = $derived(source().preconfigurationPending)
  const submissionPhase = $derived(source().submissionPhase)
  const caseIdByOrderId = $derived(source().caseIdByOrderId)
  const allowRemoveOnlySubmission = $derived(source().allowRemoveOnlySubmission)
  const buildCommitPayloads = $derived(source().buildCommitPayloads)
  const createButtonLabel = $derived(source().createButtonLabel)
  const successMessage = $derived(source().successMessage)
  const pendingContactOrders = $derived(
    localEvents.filter(
      (event) =>
        contactOrderPlanningEventAllowsInteraction(event.id, rowPresentationByEventId) &&
        !scheduleEventHasContactOrder(event, localContactOrders) &&
        !scheduleEventHasCompletedCustomerInformation(event) &&
        Boolean(event.work_order_id),
    ),
  )
  const pendingCount = $derived(pendingContactOrders.length)
  const pendingEventIds = $derived(new Set(pendingContactOrders.map((event) => event.id)))
  const effectiveRemovedEventIds = $derived(
    removedEventIds.filter((eventId) => pendingEventIds.has(eventId)),
  )
  const totalCount = $derived(localEvents.length)
  const contactStepLocked = $derived(
    Boolean(readOnly || locked) ||
      organizationId !== draft.organizationId ||
      (totalCount > 0 && pendingCount === 0),
  )
  const submissionPending = $derived(submissionPhase !== "idle" || preconfigurationPending)
  const tableLoading = $derived(
    buildContactOrderTableLoading({
      loading,
      previewLoading: planningInitialPreviewLoading,
    }),
  )
  const employeeLabelById = $derived(
    Object.fromEntries(employeeOptions.map((employee) => [employee.value, employee.label])),
  )
  const workOrderById = $derived(
    Object.fromEntries(localWorkOrders.map((order) => [order.id, order])),
  )
  const propertyById = $derived(
    Object.fromEntries(properties.map((property) => [property.id, property])),
  )
  const customersById = $derived(
    Object.fromEntries(localCustomers.map((customer) => [customer.id, customer])),
  )
  const localContactPoints = $derived(
    mergeRecipientContactPoints(contactPoints, localContactPointUpdates, deletedContactPointIds),
  )
  const contactPointsByCustomerId = $derived(groupContactPointsByCustomerId(localContactPoints))
  const appointmentRows: ContactOrderAppointmentRow[] = $derived(
    buildContactOrderAppointmentRows({
      plannedAppointments: localEvents,
      createdOrderById: workOrderById,
      propertyById,
      employeeLabelById,
      tagContext: { propertyById, customersById, contactPointsByCustomerId },
      planningPreviewByEventId,
      planningPreviewErrorsByEventId,
      dirtyScheduleEventIds,
    }),
  )
  const isEventRemoved = (eventId: string) => removedEventIds.includes(eventId)
  const isRowRemoved = (row: ContactOrderAppointmentRow) => isEventRemoved(row.event.id)
  const rowIsInteractionDisabled = (row: ContactOrderAppointmentRow) =>
    !contactOrderPlanningRowAllowsInteraction(row, rowPresentationByEventId)
  const isConfirmationInformationRow = (row: ContactOrderAppointmentRow) =>
    String(row.customerInformationMode ?? "").startsWith("confirmation")
  const displayAppointmentRows = $derived(
    appointmentRows.map((row) => ({
      ...row,
      removed: isRowRemoved(row),
    })),
  )
  const loadingRows = $derived<ContactOrderTableRow[]>(
    buildContactOrderLoadingRows(tableLoading, totalCount),
  )
  const selectedCustomerIdsForRow = (row: ContactOrderAppointmentRow) =>
    selectedCustomerIdsForPlanningRow(row, selectionState, allowedContactChannels)
  const selectedDigitalChannelsForRow = (row: ContactOrderAppointmentRow) =>
    isRowRemoved(row) || isConfirmationInformationRow(row)
      ? []
      : selectedDigitalChannelsForPlanningRow(row, selectionState, allowedContactChannels)
  const communicationSelectionForRow = (row: ContactOrderAppointmentRow) =>
    isRowRemoved(row) || isConfirmationInformationRow(row)
      ? { digitalByCustomerId: {}, postal: { mode: "DISABLED" as const } }
      : communicationSelectionForPlanningRow(row, selectionState, allowedContactChannels)
  const visibleAppointmentRows = $derived(
    filterContactOrderPlanningRows(
      displayAppointmentRows,
      contactFilters,
      selectedCustomerIdsForRow,
      selectedDigitalChannelsForRow,
    ),
  )
  const actionableSelectedIds = $derived(
    pruneContactOrderPlanningSelectedIds({
      selectedIds,
      visibleRows: visibleAppointmentRows,
      presentationByEventId: rowPresentationByEventId,
    }),
  )
  const tableRows = $derived<ContactOrderTableRow[]>(
    tableLoading ? loadingRows : visibleAppointmentRows,
  )
  const digitalChannelTogglesForRow = (row: ContactOrderAppointmentRow) =>
    buildDigitalChannelTogglesForContactOrderRow({
      row,
      digitalPlan: communicationSelectionForRow(row).digitalByCustomerId,
      allowedChannels: allowedContactChannels,
      pending: isAnnouncementPlanPending(row),
    })
  const postalOptionsForRow = (row: ContactOrderAppointmentRow) =>
    buildPostalToggleOptionsForContactOrderRow(
      row,
      allowedContactChannels,
      draft.configuredPostalFor(String(row.id)) ?? communicationSelectionForRow(row).postal,
    )

  const configuredPostalSelectionForRow = (
    row: ContactOrderAppointmentRow,
  ): ContactOrderPostalSelection => {
    const active = communicationSelectionForRow(row).postal

    if (active.mode !== "DISABLED") return active

    const saved = draft.configuredPostalFor(String(row.id))

    if (saved) return saved

    const options = postalOptionsForRow(row)
    const option =
      options.find((option) => option.mode === "PERSONAL" && !option.disabled) ??
      options.find((option) => option.mode !== "DISABLED" && !option.disabled)
    const selection = option ? postalSelectionFromValue(option.value, [option]) : null

    return selection
      ? {
          ...selection,
          dispatch_mode:
            selection.mode === "PERSONAL" && row.planningPreview?.automatic_postal_available
              ? (row.planningPreview.postal_dispatch_default ?? "MANUAL")
              : "MANUAL",
        }
      : { mode: "DISABLED" }
  }
  const pendingRows = $derived(
    filterPendingContactOrderRows(appointmentRows).filter((row) => !rowIsInteractionDisabled(row)),
  )
  const pendingRowIds = $derived(new Set(pendingRows.map((row) => String(row.id))))
  const applyTargetResolution = $derived(
    resolveBatchApplyTargets({
      filteredRows: visibleAppointmentRows.filter(
        (row) =>
          pendingRowIds.has(String(row.id)) && !row.removed && !isConfirmationInformationRow(row),
      ),
      selectedIds: actionableSelectedIds,
      getId: (row) => String(row.id),
    }),
  )
  const activePlanPendingRows = $derived(
    pendingRows.filter(
      (row) =>
        !isRowRemoved(row) &&
        (isConfirmationInformationRow(row) ||
          hasActiveCommunicationForPlanningRow(row, selectionState, allowedContactChannels)),
    ),
  )
  const skippedPendingCount = $derived(pendingRows.length - activePlanPendingRows.length)
  const pendingContactableCount = $derived(activePlanPendingRows.length)
  const skippedWarningText = $derived(buildSkippedWarningText(skippedPendingCount))
  const createIntent = $derived(
    buildContactOrderCreateIntent({
      loading: tableLoading || preconfigurationPending,
      previewPendingEventIds: planningPreviewPendingEventIds,
      locked: contactStepLocked,
      pendingContactableCount,
      pendingContactOrders,
      appointmentRows,
      selectionState,
      allowedContactChannels,
      removedEventIds: effectiveRemovedEventIds,
      allowRemoveOnlySubmission,
      buildPayloads: buildCommitPayloads,
      caseIdByOrderId,
      events: localEvents,
      contactOrders: localContactOrders,
      workOrders: localWorkOrders,
      label: createButtonLabel,
      successMessage,
    }),
  )
  const filterFields = $derived(
    buildContactOrderFilterFields({
      filters: contactFilters,
      rowCount: appointmentRows.length,
      hasUsefulSearch: hasUsefulBatchSearch,
      hasUsefulSelectOptions: hasUsefulBatchSelectOptions,
    }),
  )
  const showMetrics = $derived(
    buildContactOrderMetricsVisible({
      hasEstimate: Boolean(planningCostBenefit),
      pendingCount,
      skippedPendingCount,
      loading: loading || planningInitialPreviewLoading,
    }),
  )
  const costBenefitLoading = $derived(
    loading || planningInitialPreviewLoading || planningPreviewPendingEventIds.length > 0,
  )
  const costBenefitError = $derived(
    planningPreviewAttempted &&
      !costBenefitLoading &&
      pendingCount > 0 &&
      planningCostBenefit === null
      ? "Kosten-Nutzen-Schätzung konnte nicht berechnet werden. Bitte prüfen Sie die Preview-Antwort."
      : null,
  )
  const appointmentsRefresh = $derived(
    [
      buildContactOrderAppointmentsRefresh(appointmentRows),
      allowedContactChannels.join(","),
      Object.entries(rowPresentationByEventId)
        .sort(([left], [right]) => left.localeCompare(right))
        .map(
          ([eventId, presentation]) =>
            `${eventId}:${presentation.rowNumber ?? ""}:${presentation.disabled ? "disabled" : "enabled"}:${presentation.selectable === false ? "fixed" : "selectable"}:${presentation.muted ? "muted" : "plain"}`,
        )
        .join("|"),
    ].join("::"),
  )
  const isAnnouncementPlanPending = (row: ContactOrderAppointmentRow) =>
    planningPreviewPendingEventIds.includes(row.event.id)

  return {
    get pendingContactOrders() {
      return pendingContactOrders
    },
    get pendingCount() {
      return pendingCount
    },
    get pendingEventIds() {
      return pendingEventIds
    },
    get effectiveRemovedEventIds() {
      return effectiveRemovedEventIds
    },
    get totalCount() {
      return totalCount
    },
    get contactStepLocked() {
      return contactStepLocked
    },
    get submissionPending() {
      return submissionPending
    },
    get tableLoading() {
      return tableLoading
    },
    get employeeLabelById() {
      return employeeLabelById
    },
    get workOrderById() {
      return workOrderById
    },
    get propertyById() {
      return propertyById
    },
    get customersById() {
      return customersById
    },
    get localContactPoints() {
      return localContactPoints
    },
    get contactPointsByCustomerId() {
      return contactPointsByCustomerId
    },
    get appointmentRows() {
      return appointmentRows
    },
    get isEventRemoved() {
      return isEventRemoved
    },
    get isRowRemoved() {
      return isRowRemoved
    },
    get rowIsInteractionDisabled() {
      return rowIsInteractionDisabled
    },
    get isConfirmationInformationRow() {
      return isConfirmationInformationRow
    },
    get displayAppointmentRows() {
      return displayAppointmentRows
    },
    get loadingRows() {
      return loadingRows
    },
    get selectedCustomerIdsForRow() {
      return selectedCustomerIdsForRow
    },
    get selectedDigitalChannelsForRow() {
      return selectedDigitalChannelsForRow
    },
    get communicationSelectionForRow() {
      return communicationSelectionForRow
    },
    get visibleAppointmentRows() {
      return visibleAppointmentRows
    },
    get actionableSelectedIds() {
      return actionableSelectedIds
    },
    get tableRows() {
      return tableRows
    },
    get digitalChannelTogglesForRow() {
      return digitalChannelTogglesForRow
    },
    get postalOptionsForRow() {
      return postalOptionsForRow
    },
    get configuredPostalSelectionForRow() {
      return configuredPostalSelectionForRow
    },
    get pendingRows() {
      return pendingRows
    },
    get pendingRowIds() {
      return pendingRowIds
    },
    get applyTargetResolution() {
      return applyTargetResolution
    },
    get activePlanPendingRows() {
      return activePlanPendingRows
    },
    get skippedPendingCount() {
      return skippedPendingCount
    },
    get pendingContactableCount() {
      return pendingContactableCount
    },
    get skippedWarningText() {
      return skippedWarningText
    },
    get createIntent() {
      return createIntent
    },
    get filterFields() {
      return filterFields
    },
    get showMetrics() {
      return showMetrics
    },
    get costBenefitLoading() {
      return costBenefitLoading
    },
    get costBenefitError() {
      return costBenefitError
    },
    get appointmentsRefresh() {
      return appointmentsRefresh
    },
    get isAnnouncementPlanPending() {
      return isAnnouncementPlanPending
    },
  }
}
